/* aviaf — CR/Alerts/Ops REAL data layer.
 *
 * Replaces the design export's mock `window.OpsStore` (data-ops.jsx) with a
 * live layer wired to the real hub API.  Same store SHAPE the ported design
 * components read (arrays + subscribe + journalled mutations), but every array
 * is fetched/derived from real rows — and wherever the backend has no source
 * yet the value is an explicit null that the UI renders as an honest
 * "not yet reported" empty state.  NO fabricated numbers (§3.6).
 *
 * Contracts encoded here (DESIGN_BRIEF):
 *   §3.1 crit acks are operator-only — the server returns 403 for machine
 *        principals; the client pre-checks so the button can explain it.
 *   §3.2 dedupe groups (collapse repeats, keep the count; never resolves).
 *   §3.4 every mutation lands a techops_actions row (POST /api/techops/actions)
 *        or hits an endpoint that journals server-side.
 *   §3.6 no fabricated data — unwired fields are null → empty state.
 *
 * Timestamps: the hub stores ts_us (microseconds); the design helpers/format
 * functions all work in JS ms, so every transform divides by 1000 at the
 * boundary (US()).
 */
(() => {
const US = (us) => (us == null || Number.isNaN(Number(us)) ? null : Number(us) / 1000);
const H = 3600000;

/* ── formatting helpers (ms) — parity with the mock so ops-ui/pages work ── */
window.OPS_NOW = Date.now();
window.fmtAge = (ts) => {
  if (ts == null) return "—";
  let s = Math.max(0, (Date.now() - ts) / 1000);
  if (s < 60) return Math.floor(s) + "s";
  if (s < 3600) return Math.floor(s / 60) + "m";
  if (s < 86400) return Math.floor(s / 3600) + "h " + Math.floor((s % 3600) / 60) + "m";
  return Math.floor(s / 86400) + "d " + Math.floor((s % 86400) / 3600) + "h";
};
window.fmtTs = (ts) => {
  if (ts == null) return "—";
  const d = new Date(ts), p = (n) => String(n).padStart(2, "0");
  const today = new Date();
  const sameDay = d.getUTCDate() === today.getUTCDate() && d.getUTCMonth() === today.getUTCMonth();
  const hm = p(d.getUTCHours()) + ":" + p(d.getUTCMinutes()) + "Z";
  return sameDay ? hm : p(d.getUTCMonth() + 1) + "-" + p(d.getUTCDate()) + " " + hm;
};
window.fmtTok = (n) => (n == null || Number.isNaN(Number(n))) ? "—"
  : n >= 1e6 ? (n / 1e6).toFixed(n % 1e6 ? 1 : 0) + "M"
  : n >= 1000 ? Math.round(n / 1000) + "k" : String(n);
window.nextTickUtc = () => {
  const d = new Date();
  const t = new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate(), 21, 15));
  if (t.getTime() < Date.now()) t.setUTCDate(t.getUTCDate() + 1);
  return t.getTime();
};
window.isHumanActor = (a) => typeof a === "string" && a.startsWith("operator:");

/* Documented lane escalation note (from brief §5 — a spec fact, not data). */
const LANE_MODEL_NOTES = { "cr-nightly": "opus on named triggers (armed = approval)" };
const LANE_DEFAULT_MODEL = {
  techops: "sonnet", "cr-nightly": "sonnet", "ops-review": "opus",
  strategic: "fable", "cr-deep": "opus", "on-demand": "sonnet",
};

/* ── fetch helpers ── */
async function jget(path) {
  try {
    const r = await fetch(path, { credentials: "same-origin" });
    if (!r.ok) return null;
    return await r.json();
  } catch (_) { return null; }
}
async function jsend(path, method, body) {
  try {
    const r = await fetch(path, {
      method, credentials: "same-origin",
      headers: body ? { "content-type": "application/json" } : undefined,
      body: body ? JSON.stringify(body) : undefined,
    });
    let j = null;
    try { j = await r.json(); } catch (_) {}
    return { ok: r.ok, status: r.status, body: j };
  } catch (e) { return { ok: false, status: 0, body: null, error: String(e) }; }
}

/* ── transforms (real rows → design store shapes) ── */

function laneFromRunId(id, kind) {
  const s = String(id || kind || "");
  if (s.startsWith("kb-loop")) return "cr-nightly";
  if (s.startsWith("cr-deep")) return "cr-deep";
  if (s.startsWith("ondemand") || s.startsWith("on-demand")) return "on-demand";
  if (s.startsWith("strategic")) return "strategic";
  return "on-demand";
}
function firstSentence(t) {
  if (!t) return "";
  const s = String(t).trim();
  const m = s.match(/^.*?[.!?](\s|$)/);
  return (m ? m[0] : s).trim();
}

/* Alerts: collapse repeats by (instance, source, code) into one representative
 * (§3.2 dedupe groups), keep a count, keep the earliest ts as "since".  A group
 * shows UNACKED if ANY member is unacked, so a fresh crit never hides behind an
 * older acked duplicate (§3.3 open-crit visibility). */
function transformAlerts(raw, incidentByAlertPk) {
  const rows = (raw || []).map((r) => ({
    pk: r.alert_pk, ts: US(r.ts_us), sev: r.severity, source: r.source,
    code: r.code, instance: r.instance_id, account: r.account_label,
    msg: r.message, acknowledged: !!r.acknowledged,
    ack_user: r.ack_user, ack_ts: US(r.ack_ts_us),
  }));
  const groups = new Map();
  for (const r of rows) {
    const key = [r.instance, r.source, r.code].join(" ");
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key).push(r);
  }
  const out = [];
  for (const g of groups.values()) {
    g.sort((a, b) => (b.ts || 0) - (a.ts || 0));
    const rep = g[0];
    const anyUnacked = g.some((x) => !x.acknowledged);
    const acked = g.filter((x) => x.acknowledged).sort((a, b) => (b.ack_ts || 0) - (a.ack_ts || 0))[0];
    const since = Math.min(...g.map((x) => x.ts || Date.now()));
    const incident = g.map((x) => incidentByAlertPk[x.pk]).find(Boolean) || null;
    out.push({
      pk: rep.pk, ts: rep.ts, sev: rep.sev, source: rep.source, code: rep.code,
      instance: rep.instance, account: rep.account, msg: rep.msg,
      ack: anyUnacked ? null : (acked ? {
        by: acked.ack_user || "?", ts: acked.ack_ts || acked.ts,
        via: String(acked.ack_user || "").startsWith("dedup:") ? "inherit" : undefined,
      } : null),
      repeats: { n: g.length, since },
      incident,
      _pks: g.map((x) => x.pk),
    });
  }
  out.sort((a, b) => (b.ts || 0) - (a.ts || 0));
  return out;
}

function transformActions(raw) {
  return (raw || []).map((r) => ({
    pk: r.action_pk, incident: r.incident_id, ts: US(r.ts_us), actor: r.actor,
    kind: r.kind, tier: r.tier, status: r.status,
    trigger: r.trigger_alert_pk, instance: r.instance_id, account: r.account_label,
    args: r.args || null, outcome: r.outcome, notes: r.notes,
    updated: US(r.updated_us || r.ts_us),
    run: (r.args && (r.args.run || r.args.run_id)) || null,
  }));
}

/* Incidents come pre-grouped from /api/techops/incidents.  No title column
 * exists — derive it honestly from the trigger alert (source/code/message) or
 * the first journalled action, never invent one. */
function transformIncidents(raw, alertsByPk) {
  return (raw || []).map((inc) => {
    const acts = inc.actions || [];
    const firstInst = (acts.find((a) => a.instance_id) || {}).instance_id || null;
    const trig = inc.trigger_alert_pk;
    const ta = trig != null ? alertsByPk[trig] : null;
    let title;
    if (ta) title = `${ta.source}/${ta.code || ""} — ${String(ta.message || "").slice(0, 80)}`;
    else {
      const withOut = acts.find((a) => a.outcome);
      title = withOut ? String(withOut.outcome).slice(0, 90) : (inc.incident_id || "incident");
    }
    return {
      id: inc.incident_id,
      title,
      opened: US(inc.started_us),
      status: inc.open ? "open" : "closed",
      closed: inc.open ? undefined : US(inc.last_us),
      instance: firstInst,
      trigger: trig,
    };
  });
}

/* research_runs.summary_json is an arbitrary blob — the node may write a prose
 * string OR a structured object.  Pull structured fields when present, degrade
 * to prose/notes otherwise.  Nothing is fabricated. */
function transformRuns(raw) {
  return (raw || []).map((r) => {
    const sj = r.summary;
    const obj = (sj && typeof sj === "object") ? sj : null;
    const prose = (typeof sj === "string") ? sj : (obj && (obj.summary || obj.text)) || r.notes || "";
    const lane = (obj && obj.lane) || laneFromRunId(r.run_id, r.kind);
    return {
      id: r.run_id, kind: r.kind, lane,
      model: (obj && obj.model) || LANE_DEFAULT_MODEL[lane] || "sonnet",
      started: US(r.started_us), finished: US(r.finished_us),
      status: r.finished_us ? "finalized" : "running",
      head: (obj && obj.head) || firstSentence(prose) || r.kind,
      summary: prose,
      metrics: (obj && obj.metrics) || (obj && (obj.tokens || obj.sessions) ? obj : null),
      triggers: (obj && obj.triggers) || null,
      candidates: (obj && obj.candidates) || [],
      recs: (obj && obj.recs) || [],
      incident: (obj && obj.incident) || null,
      request: (obj && obj.request) || null,
    };
  });
}

function transformCandidates(raw, actions) {
  return (raw || []).map((c) => {
    const om = c.observation_metrics || null;
    const pick = (o, ks) => { for (const k of ks) if (o && o[k] != null) return o[k]; return undefined; };
    let obs = null;
    if (om) {
      const trades = pick(om, ["trades", "n", "n_trades"]);
      const pf = pick(om, ["pf", "profit_factor"]);
      const er = pick(om, ["er", "e_r", "expectancy"]);
      const worstDay = pick(om, ["worstDay", "worst_day"]);
      const days = pick(om, ["days", "days_in_status"]);
      if ([trades, pf, er, worstDay, days].some((v) => v != null)) {
        obs = {
          trades: trades ?? 0, pf: Number(pf ?? 0), er: Number(er ?? 0),
          worstDay: Number(worstDay ?? 0), days: days ?? 0,
        };
      }
    }
    const uiStatus = c.status === "live" ? "live-recommended" : c.status;
    const decision = (actions || []).find(
      (a) => a.kind === "candidate_promote" && a.args && a.args.candidate === c.candidate_id
        && a.status === "proposed");
    return {
      id: c.candidate_id, strategy: c.strategy, status: uiStatus,
      created: US(c.created_us),
      statusSince: US(c.retired_us || c.promoted_us || c.created_us),
      permanent: /permanent/i.test(String(c.notes || "")) || !!(om && om.permanent),
      params: c.params || {},
      obs,
      spark: (om && (om.spark || om.equity_curve)) || null,
      notes: c.notes || "",
      decision: decision ? decision.pk : undefined,
    };
  });
}

/* Lanes from v14 lane_policy.  Budgets are real + editable; per-lane SPEND has
 * no hub source yet, so spend is null → the UI renders "spend not reported". */
function transformLanes(raw, actions) {
  return (raw || []).map((l) => {
    const lastAct = (actions || [])
      .filter((a) => a.args && a.args.lane === l.lane_id)
      .sort((a, b) => (b.ts || 0) - (a.ts || 0))[0];
    return {
      id: l.lane_id, t: l.tier, label: l.lane_id, trigger: l.trigger,
      model: l.model, modelNote: LANE_MODEL_NOTES[l.lane_id] || null,
      cadence: l.cadence, enabled: !!l.enabled, killed: !!l.killed,
      budget: {
        sessions: l.sessions_budget, sessionsUnit: l.sessions_unit,
        tokens: l.tokens_budget, tokensUnit: l.tokens_unit,
      },
      spend: null,                                   // §3.6 honest empty
      may: l.may || "",
      updated_us: US(l.updated_us), updated_by: l.updated_by,
      lastActivity: lastAct ? lastAct.ts : null,
      lastNote: lastAct ? (lastAct.outcome || lastAct.kind) : null,
    };
  });
}

function transformNode(policy, status, researchState) {
  const pol = policy ? {
    node: policy.node_id, model: policy.model, effort: policy.effort,
    max_sessions_per_day: policy.max_sessions_per_day,
    techops_tier: policy.techops_tier,
    cr_enabled: policy.cr_enabled, techops_enabled: policy.techops_enabled,
    updated_by: policy.updated_by, updated_us: US(policy.updated_us),
  } : null;
  const rsDetail = (researchState && researchState.payload) || researchState || {};
  const d = (status && status.detail) || {};
  const kbOpen = d.kb_open_questions ?? d.n_open_questions ?? rsDetail.n_open_questions;
  const kbBlocked = d.kb_blocked_questions ?? d.n_blocked_questions ?? rsDetail.n_blocked_questions;
  const st = (status && status.ts_us) ? {
    ts: US(status.ts_us), repo_sha: status.repo_sha || "—",
    repo_age_days: status.repo_age_days,
    last_tick: US(status.last_tick_us), last_tick_result: status.last_tick_result || "—",
    sessions_today: status.sessions_today ?? 0,
    detail: { tokens_today: d.tokens_today ?? null, kb_open_questions: kbOpen ?? null,
              kb_blocked_questions: kbBlocked ?? null },
  } : null;
  return { policy: pol, status: st };
}

function transformFleet(instances, accountsByInst, healthByInst) {
  return (instances || []).map((i) => {
    const id = i.instance_id;
    const readOnly = /alpaca/i.test(id);
    const state = i.connected ? "connected" : "silent";
    const accts = (accountsByInst[id] || []).map((a) => a.label || a.account_label).filter(Boolean);
    const health = healthByInst[id] || null;
    return {
      id,
      kind: readOnly ? "equities monitor · read-only" : (i.hostname ? `engine · ${i.hostname}` : "trading engine"),
      state, sha: "—", ver: i.version || "—",
      lastSeen: US(i.last_seen_us), since: null,
      accounts: accts.length ? accts : ["—"],
      loopLag: health && health.series && health.series.length ? health.series : null,
      loopLagMax: health ? health.max : null, loopLagLast: health ? health.last : null,
      commands: !!i.connected, readOnly,
      recovery: null,
      reason: state === "silent"
        ? "engine unreachable — a dead process cannot receive commands; host-side recovery armed"
        : null,
      frozen: null,                                  // §3.6: no equity snapshot source wired here
      logs: null,                                    // lazy-loaded from /logfile on demand
    };
  });
}

/* Per-source 24h alert counts + severity-taxonomy lint, derived from the raw
 * alert rows.  Flood-cap ("CAPPED 8/h") is instance-side and NOT visible to
 * the hub — we say so rather than guess (§4.2). */
function transformNoise(rawAlerts) {
  const now = Date.now(), cutoff = now - 24 * H;
  const by = new Map();
  for (const r of rawAlerts || []) {
    const ts = US(r.ts_us);
    if (ts == null || ts < cutoff) continue;
    const s = r.source || "?";
    if (!by.has(s)) by.set(s, { source: s, h24: 0, legacy: false });
    const e = by.get(s);
    e.h24 += 1;
    if (r.severity === "warning") e.legacy = true;
  }
  return [...by.values()].sort((a, b) => b.h24 - a.h24)
    .map((e) => ({ ...e, capped: null, cappedAt: null }));  // capped: unknown to hub
}

window.__OPS_TRANSFORMS = { transformAlerts, transformActions, transformIncidents };

/* ── store ── */
const subs = new Set();
const S = window.OpsStore = {
  alerts: [], actions: [], incidents: [], lanes: [], node: { policy: null, status: null },
  runs: [], candidates: [], fleet: [], spendHistory: [], noise: [], mutes: [],
  slaMinutes: 15, staleMinutes: 60, version: 0, drawer: null,
  loaded: false, lastError: null,
  subscribe(fn) { subs.add(fn); ensurePolling(); return () => subs.delete(fn); },
  _emit() { S.version++; subs.forEach((f) => { try { f(S.version); } catch (_) {} }); },
  _user() {
    const s = window.__OPS_SESSION;
    const sub = s && (s.subject || s.sub);
    if (!sub) return "operator:admin";
    return sub.startsWith("operator:") ? sub : "operator:" + sub;
  },
  refresh,
  async journal(row) {
    const body = {
      kind: row.kind, tier: row.tier || "safe", status: row.status || "done",
      incident_id: row.incident || undefined, instance_id: row.instance || undefined,
      account_label: row.account || undefined, trigger_alert_pk: row.trigger || undefined,
      args: row.args || undefined, outcome: row.outcome || undefined, notes: row.notes || undefined,
    };
    const res = await jsend("/api/techops/actions", "POST", body);
    if (!res.ok) window.pushToast && window.pushToast({ kind: "error", title: "journal failed", msg: `HTTP ${res.status}` });
    await refresh();
    return res.body || {};
  },
  async ackAlert(pk, reason) {
    const a = S.alerts.find((x) => x.pk === pk) || {};
    if (a.sev === "crit" && !window.isHumanActor(S._user())) {
      window.pushToast && window.pushToast({ kind: "error", title: "crit ack refused", msg: "operator-only (§3.1)" });
      return { ok: false, code: 403 };
    }
    const res = await jsend(`/api/alerts/${pk}/ack`, "POST");
    if (res.status === 403) window.pushToast && window.pushToast({ kind: "error", title: "crit ack refused", msg: "server: operator-only (§3.1)" });
    else if (!res.ok) window.pushToast && window.pushToast({ kind: "error", title: "ack failed", msg: `HTTP ${res.status}` });
    else if (reason && reason.trim()) {
      await jsend("/api/techops/actions", "POST", {
        kind: "ack", incident_id: a.incident || undefined, trigger_alert_pk: pk,
        instance_id: a.instance || undefined, outcome: `acked #${pk} · reason: ${reason.trim()}`,
      });
    }
    await refresh();
    return { ok: res.ok };
  },
  async unackAlert(pk) {
    // No un-ack endpoint in phase 1 — journal the operator's intent (audited)
    // and tell them plainly rather than fake a state flip.
    const a = S.alerts.find((x) => x.pk === pk) || {};
    await S.journal({ kind: "incident_reopen", incident: a.incident, trigger: pk,
      instance: a.instance, outcome: `un-ack requested for #${pk} (phase-1: recorded, alert reactivation is operator-DB)` });
    window.pushToast && window.pushToast({ kind: "info", title: "un-ack recorded", msg: "no un-ack endpoint in phase 1 — logged to the Ops timeline" });
  },
  async bulkAck(_sev) {
    const res = await jsend("/api/alerts/ack-all?exclude_crit=true", "POST");
    if (!res.ok) window.pushToast && window.pushToast({ kind: "error", title: "bulk-ack failed", msg: `HTTP ${res.status}` });
    else window.pushToast && window.pushToast({ kind: "success", title: "bulk-acked", msg: `${(res.body && res.body.acked) || 0} non-crit alerts` });
    await refresh();
  },
  async openIncident(alertPk) {
    const a = S.alerts.find((x) => x.pk === alertPk) || {};
    await S.journal({ kind: "alert_attach", trigger: alertPk, instance: a.instance,
      outcome: `incident opened from alert #${alertPk}` });
  },
  async resolveIncident(id) {
    await S.journal({ kind: "resolve", incident: id, outcome: "incident closed by operator" });
  },
  async approve(pk) {
    const r = S.actions.find((x) => x.pk === pk);
    if (!r) return;
    await S.journal({ kind: "approval", incident: r.incident, tier: "safe", status: "done",
      args: { decision_pk: pk, kind: r.kind }, outcome: `approved #${pk} ${r.kind} (${r.tier})` });
    window.pushToast && window.pushToast({ kind: "success", title: "approved", msg: `#${pk} journalled` });
  },
  async deny(pk, reason) {
    const r = S.actions.find((x) => x.pk === pk);
    if (!r) return;
    await S.journal({ kind: "denial", incident: r.incident, tier: "safe", status: "done",
      args: { decision_pk: pk, kind: r.kind, reason: reason || "—" },
      outcome: `denied #${pk} ${r.kind} · reason: ${reason || "—"}` });
  },
  async setLane(id, patch, label) {
    const body = {};
    if (patch.top) {
      if ("enabled" in patch.top) body.enabled = patch.top.enabled;
      if ("killed" in patch.top) body.killed = patch.top.killed;
      if ("model" in patch.top) body.model = patch.top.model;
    }
    if (patch.budget) {
      if ("sessions" in patch.budget) body.sessions_budget = patch.budget.sessions;
      if ("tokens" in patch.budget) body.tokens_budget = patch.budget.tokens;
    }
    const res = await jsend(`/api/node/lanes/${encodeURIComponent(id)}`, "PUT", body);
    if (res.status === 403) window.pushToast && window.pushToast({ kind: "error", title: "lane edit refused", msg: "operator-only" });
    else if (!res.ok) window.pushToast && window.pushToast({ kind: "error", title: "lane edit failed", msg: `HTTP ${res.status}` });
    else window.pushToast && window.pushToast({ kind: "success", title: "lane updated", msg: label || id });
    await refresh();
  },
  async setMaster(key, v) {
    const res = await jsend("/api/node/policy", "PUT", { [key]: !!v });
    if (res.status === 403) window.pushToast && window.pushToast({ kind: "error", title: "policy edit refused", msg: "operator-only" });
    else if (!res.ok) window.pushToast && window.pushToast({ kind: "error", title: "policy edit failed", msg: `HTTP ${res.status}` });
    await refresh();
  },
  async setCandidate(id, to) {
    if (to === "live-recommended") {
      await S.journal({ kind: "candidate_promote", tier: "destructive", status: "proposed",
        args: { candidate: id, to }, notes: `operator-initiated promotion of ${id} — awaiting approval in decision queue` });
      return;
    }
    const res = await jsend(`/api/research/candidates/${encodeURIComponent(id)}/status`, "POST", { status: to });
    if (!res.ok) window.pushToast && window.pushToast({ kind: "error", title: "candidate update failed", msg: `HTTP ${res.status}` });
    else window.pushToast && window.pushToast({ kind: "success", title: "candidate", msg: `${id} → ${to}` });
    await refresh();
  },
  async fileResearchRequest(q) {
    if (!q || !q.trim()) return;
    await S.journal({ kind: "research_request", status: "proposed", args: { question: q.trim() },
      notes: "rides the node's next policy poll — picked up at the 21:15Z tick" });
    window.pushToast && window.pushToast({ kind: "success", title: "research request filed", msg: "queued for the next tick" });
  },
  async muteSource(source, hours) {
    S.mutes = [...S.mutes.filter((m) => m.source !== source), { source, until: Date.now() + hours * H }];
    S._emit();
    await S.journal({ kind: "mute", outcome: `UI mute ${source} for ${hours}h (auto-expires · never mutes crit)` });
  },
  async unmuteSource(source) {
    S.mutes = S.mutes.filter((m) => m.source !== source);
    S._emit();
    await S.journal({ kind: "mute", outcome: `unmuted ${source}` });
  },
  async fleetLogs(id) {
    return await jget(`/api/instances/${encodeURIComponent(id)}/logfile?tail=120`);
  },
  /* Real fleet commands (§4.3) — hit the existing command endpoints, which
   * journal server-side (§3.4).  A dead engine 503s (§3.7): surface it, never
   * fake success.  pause/resume need an account_label, so they fan out over
   * the instance's real account labels; restart/config_reload are
   * instance-level.  restart requires confirm_label=instance_id. */
  async sendCommand(instance, kind, accounts) {
    const enc = encodeURIComponent;
    let results = [];
    try {
      if (kind === "restart") {
        results.push(await jsend(`/api/commands/${enc(instance)}/restart?confirm_label=${enc(instance)}`, "POST"));
      } else if (kind === "config_reload") {
        results.push(await jsend(`/api/commands/${enc(instance)}/config_reload`, "POST"));
      } else if (kind === "pause" || kind === "resume") {
        const labels = (accounts || []).filter((a) => a && a !== "—");
        if (!labels.length) {
          window.pushToast && window.pushToast({ kind: "warn", title: `${kind} — no account`, msg: "no account label on this instance card; use the instance page" });
        }
        for (const lbl of labels) {
          results.push(await jsend(`/api/commands/${enc(instance)}/${kind}?account_label=${enc(lbl)}`, "POST"));
        }
      }
    } catch (e) { results.push({ ok: false, status: 0, error: String(e) }); }
    const bad = results.find((r) => !r.ok);
    if (bad) {
      const why = bad.status === 503 ? "engine unreachable — host-side recovery armed (§3.7)" : `HTTP ${bad.status}`;
      window.pushToast && window.pushToast({ kind: "error", title: `${kind} failed`, msg: why });
    } else if (results.length) {
      window.pushToast && window.pushToast({ kind: "success", title: `${kind} dispatched`, msg: `${instance} · COMMAND_ACK ok` });
    }
    await refresh();
    return { ok: !bad };
  },
};

/* ── poll / refresh ── */
let polling = false, timer = null, inFlight = false;
async function refresh() {
  if (inFlight) return;
  inFlight = true;
  try {
    const [alertsRaw, actionsRaw, incRaw, runsRaw, candRaw, lanesRaw, policy, status,
           instances, accounts, healthEvents, rsEvents] = await Promise.all([
      window.ALERTS && window.ALERTS.length ? Promise.resolve(window.ALERTS) : jget("/api/alerts?limit=400"),
      jget("/api/techops/actions?limit=500"),
      jget("/api/techops/incidents?limit=100"),
      jget("/api/research/runs?limit=100"),
      jget("/api/research/candidates?limit=200"),
      jget("/api/node/lanes"),
      jget("/api/node/policy"),
      jget("/api/node/status"),
      window.INSTANCES && window.INSTANCES.length ? null : jget("/api/instances"),
      window.ACCOUNTS && window.ACCOUNTS.length ? null : jget("/api/accounts"),
      jget("/api/events?kind=health&limit=200"),
      jget("/api/events?kind=research_state&limit=1"),
    ]);

    const rawAlerts = alertsRaw || window.ALERTS || [];
    const alertsByPk = {};
    for (const a of rawAlerts) alertsByPk[a.alert_pk] = a;

    const actions = transformActions(actionsRaw || []);
    const incidentByAlertPk = {};
    for (const inc of (incRaw || [])) {
      for (const act of (inc.actions || [])) {
        if (act.trigger_alert_pk != null && !(act.trigger_alert_pk in incidentByAlertPk)) {
          incidentByAlertPk[act.trigger_alert_pk] = inc.incident_id;
        }
      }
    }

    S.alerts = transformAlerts(rawAlerts, incidentByAlertPk);
    S.actions = actions;
    S.incidents = transformIncidents(incRaw || [], alertsByPk);
    S.runs = transformRuns(runsRaw || []);
    S.candidates = transformCandidates(candRaw || [], actions);
    S.lanes = transformLanes(lanesRaw || [], actions);
    S.node = transformNode(policy, status, (rsEvents && rsEvents[0]) || null);

    const instRows = instances || window.INSTANCES || [];
    const acctRows = accounts || window.ACCOUNTS || [];
    const accountsByInst = {};
    for (const a of acctRows) {
      const k = a.instance_id; if (!k) continue;
      (accountsByInst[k] = accountsByInst[k] || []).push(a);
    }
    const healthByInst = {};
    for (const ev of (healthEvents || [])) {
      const k = ev.instance_id; if (!k) continue;
      const p = ev.payload || {};
      const v = p.loop_lag_max_60s;
      if (v == null) continue;
      const h = (healthByInst[k] = healthByInst[k] || { series: [], last: null, max: null });
      h.series.push(Number(v));
      if (h.last == null) h.last = (p.loop_lag_last != null ? Number(p.loop_lag_last) : Number(v));
    }
    for (const k of Object.keys(healthByInst)) {
      const h = healthByInst[k];
      h.series = h.series.slice(0, 60).reverse();     // oldest→newest for the sparkline
      h.max = h.series.length ? Math.max(...h.series) : null;
    }
    S.fleet = transformFleet(instRows, accountsByInst, healthByInst);
    S.noise = transformNoise(rawAlerts);
    S.spendHistory = [];                              // §3.6: no per-lane spend source yet

    S.loaded = true;
    S._emit();
  } catch (e) {
    S.lastError = String(e);
  } finally {
    inFlight = false;
  }
}
function ensurePolling() {
  if (polling) return;
  polling = true;
  refresh();
  timer = setInterval(refresh, 6000);
}

/* ── derived globals (parity with the mock so ops-ui.jsx works unchanged) ── */
window.opsOpenCrits = () => S.alerts.filter((a) => a.sev === "crit" && !a.ack);
window.opsIncidentRows = (id) => {
  const acts = S.actions.filter((r) => r.incident === id).map((r) => ({ type: "action", ts: r.ts, r }));
  const als = S.alerts.filter((a) => a.incident === id).map((a) => ({ type: "alert", ts: a.ts, a }));
  return [...als, ...acts].sort((x, y) => (x.ts || 0) - (y.ts || 0));
};
window.opsIncidentMeta = (id) => {
  const inc = S.incidents.find((x) => x.id === id);
  if (!inc) return null;
  const rows = S.actions.filter((r) => r.incident === id);
  const lastAct = rows.length ? Math.max(...rows.map((r) => r.updated || r.ts || 0)) : (inc.opened || 0);
  const openCrit = S.alerts.some((a) => a.incident === id && a.sev === "crit" && !a.ack);
  const staleMin = (Date.now() - lastAct) / 60000;
  return {
    ...inc, actionCount: rows.length, lastAct, openCrit,
    stale: inc.status === "open" && staleMin >= S.staleMinutes,
    lastActor: rows.length ? rows.reduce((a, b) => ((a.updated || a.ts) > (b.updated || b.ts) ? a : b)).actor : "—",
  };
};
window.opsFleetState = () => {
  const down = S.fleet.some((f) => f.state === "silent");
  const deg = S.alerts.some((a) => !a.ack && (a.sev === "warn" || a.sev === "warning"));
  return down ? "down" : deg ? "degraded" : "green";
};
/* Nav crit badge (app.jsx) — count distinct unacked crit groups
 * (instance,source,code) straight off the WS-live window.ALERTS so the badge
 * stays in sync with the app's existing render cadence, not this store's 6s
 * poll.  Mirrors the triage board's collapsed view. */
window.openCritAlertCount = () => {
  const arr = window.ALERTS || [];
  const seen = new Set();
  for (const a of arr) {
    const sev = window.normalizeAlertSeverity ? window.normalizeAlertSeverity(a.severity) : a.severity;
    if (!a.acknowledged && sev === "crit") seen.add([a.instance_id, a.source, a.code].join(" "));
  }
  return seen.size;
};
})();
